//SamSelect.tsx
import {Listbox, ListboxButton, ListboxOption, ListboxOptions} from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';
import {SelectOption} from "../api/ApiTypes.ts";

interface SelectProps {
    value: any;
    onChange: (value: any) => void;
    options: SelectOption[];
    placeholder?: string;
    disabled?: boolean;
    className?: string;
}

export const SamSelect = ({
                              value,
                              onChange,
                              options,
                              placeholder = 'Select an option',
                              disabled = false,
                              className = ''
                          }: SelectProps) => {
    // Find the currently selected option for display

    const selectedOption = Array.isArray(value) ?
        options.find(opt => {
            // Check if arrays have the same length and same elements
            if (!opt?.value || !value || opt.value.length !== value.length) {
                return false;
            }

            // Convert both arrays to sets for comparison
            const optSet = new Set(opt.value);
            const valueSet = new Set(value);

            // Check if every element in optSet is in valueSet
            return [...optSet].every(val => valueSet.has(val));
        }) :
        options.find(opt => opt.value === value);

    return (
        <Listbox value={value} onChange={onChange} disabled={disabled}>
            <div className={`relative mt-1 ${className} z-60`}>
                {/* Select Button */}
                <ListboxButton
                    className={`relative w-full rounded-md border border-slate-300 py-2 pl-3 pr-10 text-left shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm
                        ${disabled
                        ? 'bg-slate-50 text-slate-700 cursor-not-allowed'
                        : 'bg-white cursor-pointer hover:bg-slate-50'
                    }`}
                >
                    <span className="block truncate">{selectedOption ? selectedOption.label : placeholder}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronDown className={`h-4 w-4 ${disabled ? 'text-slate-500' : 'text-slate-400'}`} aria-hidden="true" />
                    </span>
                </ListboxButton>

                {/* Options Dropdown - Only shown when not disabled */}
                {!disabled && (
                    <ListboxOptions className="absolute z-60 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {options.map((option) => (
                            <ListboxOption
                                key={option.id ?? option.value}
                                value={option.value}
                                className={({ focus }) => `relative cursor-pointer select-none py-2 pl-10 pr-4 ${focus ? 'bg-blue-100 text-blue-900' : 'text-slate-900'}`}
                            >
                                {({ selected }) => (
                                    <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {option.label}
                                        </span>
                                        {selected && (
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                                <Check className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                        )}
                                    </>
                                )}
                            </ListboxOption>
                        ))}
                    </ListboxOptions>
                )}
            </div>
        </Listbox>
    );
};